import { Link } from '@tanstack/react-router';
import { MoreHorizontal } from 'lucide-react';
import type { KeyboardEvent, MouseEvent, PointerEvent, ReactNode } from 'react';
import { PIPELINE_FLAG_LABELS, type PipelineRow } from '@nest-recruit/shared';
import { Avatar } from '../../../../components/Avatar';
import { Chip } from '../../../../components/Chip';
import { ToneIcon, toneCardClass } from '../../../../components/Tone';
import { STAGE_TONES } from '../../../../components/status-tones';
import { flagsOf } from '../pipeline-columns';
import styles from './BoardCard.module.css';
import { useModule } from '../../../../lib/modules';

const MS_PER_DAY = 86_400_000;
/** A card stays a card: two tags and two flags, then "+n". */
const SHOWN_CHIPS = 2;

interface BoardCardProps {
  readonly row: PipelineRow;
  readonly now: number;
  readonly isPending: boolean;
  readonly isDragging: boolean;
  readonly onDragStart: (event: PointerEvent<HTMLElement>, row: PipelineRow) => void;
  readonly onOpen: (row: PipelineRow) => void;
  readonly onMenu: (row: PipelineRow, trigger: HTMLElement) => void;
}

/**
 * One person on SCR-03 (DECISIONS-LOG S16 Q7): name, current title and employer, score,
 * days in stage, recruiter, tags and flags — never salary (§L23). Adapted from Tomo's
 * `BoardCard.tsx`: the whole card is the drag handle, and its link and ⋯ button still
 * work as themselves. Enter opens the application; the ⋯ menu moves it by keyboard.
 */
export function BoardCard({ row, now, isPending, isDragging, onDragStart, onOpen, onMenu }: BoardCardProps): ReactNode {
  const days = Math.max(0, Math.floor((now - new Date(row.stageEnteredAt).getTime()) / MS_PER_DAY));
  // R1 (§L34): days in stage is a Time figure, shown only while that module is on.
  const isDaysShown = useModule('time_metrics');
  const role = [row.candidate.currentTitle, row.candidate.currentEmployer].filter(Boolean).join(' · ');
  const flags = flagsOf(row);
  const extra = Math.max(0, row.tags.length - SHOWN_CHIPS) + Math.max(0, flags.length - SHOWN_CHIPS);

  return (
    <article
      className={[toneCardClass(true), styles.card, isDragging ? styles.dragging : '', isPending ? styles.pending : ''].filter(Boolean).join(' ')}
      data-tone={STAGE_TONES[row.stage]}
      tabIndex={0}
      aria-label={`${row.candidate.fullName}, match ${row.matchScore ?? 'not ranked'}${isDaysShown ? `, ${days} ${days === 1 ? 'day' : 'days'} in this stage` : ''}`}
      onPointerDown={(event) => onDragStart(event, row)}
      onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter') onOpen(row);
      }}
    >
      <ToneIcon tone={STAGE_TONES[row.stage]} />
      <header className={styles.head}>
        <Link to="/applications/$applicationId" params={{ applicationId: row.id }} className={styles.name} draggable={false}>
          {row.candidate.fullName}
        </Link>
        <span className={styles.score} title="Match with the ideal candidate">
          {row.matchScore ?? '—'}
        </span>
      </header>
      {role !== '' && <p className={styles.role}>{role}</p>}
      {(row.tags.length > 0 || flags.length > 0) && (
        <div className={styles.chips}>
          {row.tags.slice(0, SHOWN_CHIPS).map((tag) => (
            <Chip key={tag.id} tone="neutral" plain>
              {tag.name}
            </Chip>
          ))}
          {flags.slice(0, SHOWN_CHIPS).map((flag) => (
            <Chip key={flag} tone={flag === 'knockout' || flag === 'missing_must_have' ? 'warning' : 'neutral'} plain>
              {PIPELINE_FLAG_LABELS[flag]}
            </Chip>
          ))}
          {extra > 0 && <span className={styles.more}>+{extra}</span>}
        </div>
      )}
      <footer className={styles.foot}>
        {(isPending || isDaysShown) && <span className={styles.days}>{isPending ? 'Saving…' : `${days} ${days === 1 ? 'day' : 'days'}`}</span>}
        {row.lockedByName && <span className={styles.days}>{row.lockedByName} is sorting</span>}
        <span className={styles.spacer} />
        {row.recruiterName && <Avatar name={row.recruiterName} size="sm" />}
        <button
          type="button"
          className={styles.menu}
          aria-label={`Move or open ${row.candidate.fullName}`}
          onClick={(event: MouseEvent<HTMLButtonElement>) => onMenu(row, event.currentTarget)}
        >
          <MoreHorizontal size={16} aria-hidden />
        </button>
      </footer>
    </article>
  );
}
